import {
  type ChatQuestion,
  type ReviewRow,
  RECIPIENT,
} from '@/karehero-app/tell-us-once/intake/content';
import {
  optionsFor,
  QUESTIONS,
  QUIZ_ORDER,
  type Question,
} from '@/benefits-flow/screener/questions';
import {
  GROUPS,
  LEDGER,
} from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';

/**
 * The benefits calculator, asked the way the intake asks.
 *
 * PROPOSED. Nothing here is built: it is the calculator laid out in the patterns
 * Tell Us Once established, so the two read as one product rather than a
 * conversation followed by a form.
 *
 * The questions and their options are the real screener's, from
 * benefits-flow/screener/questions.ts, so this is the actual question set in a
 * different frame rather than a set written to suit the frame.
 *
 * Three things drive the shape:
 *
 * 1. It only asks what we do not already have. The intake filled three of the
 *    fourteen, so the calculator opens by saying so rather than asking again.
 * 2. What we do hold is confirmed rather than re-asked, with where it came from
 *    on the face of it. Confirm above change, because changing has to be as easy
 *    as confirming or people wave wrong answers through.
 * 3. The three questions a conversation must never answer are printed on a
 *    document somebody already has, so the award letter is offered where those
 *    questions start rather than at the beginning, where it would be an upload
 *    in search of a reason.
 */

/**
 * Every question, as the chat would ask it.
 *
 * Mapped off the real screener rather than written out again: the prompts, the
 * options and the order are QUIZ_ORDER's, so this page cannot drift from the
 * question set the engine actually runs. Nineteen of them, which is the point of
 * the cold path: this is what somebody who has told us nothing has to get
 * through.
 *
 * Rows for every single-choice question, however many answers it has, and the
 * pick list only where more than one answer can be true.
 *
 * The intake's own note puts rows at two or three answers, which is right for a
 * conversation carrying the occasional closed question and wrong for a screener
 * where every question is one. A row is a tap; the pick list is a tap and then
 * Next, and it moves the answering out of the transcript into a bar. At five
 * short answers that trade buys nothing. Where a Next earns its place is the
 * multi-select: ticking two things needs a way to say you are done.
 */
// The cold path is Margaret's, and she lives in England, so options offered
// only in another nation are left off.
const COLD_NATION = 'england';

/**
 * Questions asked by talking rather than by rows, in place of the screener's.
 *
 * The bus pass disability question is seven answers, which as rows runs off
 * the screen. Said in their own words it is one turn, read with everything
 * else once they finish, and a none row for the common case.
 */
const TALKED: Record<string, ChatQuestion> = {
  'care-recipient-disability-type': {
    id: 'care-recipient-disability-type',
    ask: `Does any of this apply to ${RECIPIENT}? Sight loss, hearing loss, not being able to speak, a learning disability, finding it hard to walk, or not being able to use either arm.`,
    hint: 'Say it or type it.',
    mode: 'say',
    asides: [{ label: 'None of these' }],
    answer: 'None of these',
  },
};

export const EVERY_QUESTION: ChatQuestion[] = QUIZ_ORDER.map((id) => {
  const q = QUESTIONS[id];
  // A yes/no carries no options in the screener: the answers are the type. In
  // the chat they have to be rows like any other closed question, or the
  // question arrives with nothing under it to tap.
  const options =
    q.type === 'boolean'
      ? ['Yes', 'No']
      : optionsFor(q, COLD_NATION).map((o) => o.label);
  const ask = q.prompt.replace(/\{name\}/g, RECIPIENT);
  // A pick-several answers in the transcript like a pick-one does, and stays
  // open: `multi` keeps the rows up after each tap, and the `go` row is the way
  // out of it. It used to fall through to `pick`, which is a radio list in the
  // footer bar, so a question the screener defines as multi-select could only
  // take one answer.
  const isMulti = q.type === 'multi-select';
  const rows = options.length > 0;
  const talked = TALKED[q.id];
  if (talked) return talked;
  return {
    id: q.id,
    ask,
    mode: rows ? 'rows' : 'pick',
    options,
    ...(rows
      ? {
          asides: [
            ...options.map((label) => ({ label })),
            ...(isMulti
              ? [
                  {
                    icon: 'chevron-right',
                    label: "Continue when you're done",
                    tone: 'go' as const,
                  },
                ]
              : []),
          ],
          ...(isMulti ? { multi: true } : {}),
        }
      : {}),
    answer: options[0],
  } satisfies ChatQuestion;
});

/** What the intake already answered, and where each one came from. */
export const CARRIED: { label?: string; rows: ReviewRow[] } = {
  // No label: the title above it already says what the list is.
  rows: [
    { id: 'location', label: 'Where she lives', value: 'England', from: 'ai' },
    {
      id: 'condition',
      label: 'Condition',
      value: 'Dementia, and reduced mobility',
      from: 'ai',
    },
    {
      id: 'carer',
      label: 'Who cares for her',
      value: 'You, about 40 hours a week',
      from: 'ai',
    },
  ],
};

/**
 * The offer before the questions start.
 *
 * Eleven taps is not the problem worth solving. The three questions that decide
 * the money are printed on paperwork, and so are half the rest: a pension
 * statement, a letter from a consultant, a care plan. Somebody who hands over
 * two documents at the front may not have to answer most of this.
 *
 * Offered rather than asked for, and skippable in one tap. A screen demanding
 * paperwork before it will help is a screen most people close, and the
 * questions are still the way through for anybody who has nothing to hand.
 */
export const DOCUMENTS_FIRST = {
  title: 'Send us the paperwork and answer less',
  description:
    'If you have anything on paper, we will read what we can from it and only ask about what is left.',
  examples: [
    {
      icon: 'sterling-sign',
      label: 'Pension or benefits letters',
      note: 'Award letters, statements, anything that says an amount',
    },
    {
      icon: 'file-lines',
      label: 'Letters from a doctor or hospital',
      note: 'A diagnosis, a discharge summary, a consultant letter',
    },
    {
      icon: 'id-card',
      label: 'Legal documents',
      note: 'Power of attorney, a deputyship order',
    },
    {
      icon: 'heart',
      label: 'Care assessments and care plans',
      note: 'From the council, or from a home',
    },
  ],
  primary: 'Add documents',
  skip: 'Just ask me the questions',
  /** Said once, under the list: it is the reason to bother. */
  note: 'Nothing is read for anything other than working out what you can claim.',
};

/**
 * The wait while the paperwork is read, and the read-back after it.
 *
 * It sits directly after the offer rather than at the end: the whole reason to
 * hand something over first is that it answers questions we would otherwise
 * ask, and that only pays off if the reading happens before the asking.
 */
export const DOCUMENTS_READING = {
  title: "We're reading what you sent",
  description: 'This takes a few seconds.',
};

export const DOCUMENTS_READ = {
  title: 'What we read from it',
  description: 'Change anything we got wrong. The questions left are shorter.',
  primary: 'Start the questions',
};

export const CALCULATOR_START = {
  title: 'Three of these we already have',
  primary: 'Start',
};

/**
 * The route: a confirm, the two the claim turns on, then the money questions
 * with the letter offered against them.
 */
/**
 * The paperwork offer, as the conversation's first turn.
 *
 * It was DocumentsFirstScreen: a title, four example cards, a primary and a
 * skip link. A full screen with a primary button reads as a gate to clear
 * before you are allowed to start, which is a poor first move in a thing people
 * open because they are worried about money. As a turn it is an offer you
 * answer, like everything after it, and the calculator is one surface from here
 * to the result.
 *
 * The examples survive the move, because they are what sends somebody to a
 * drawer: "paperwork" means nothing to a person holding a letter they have not
 * read properly. Their labels come across as a line, read from DOCUMENTS_FIRST
 * rather than retyped. Their notes do not — a bubble carrying four labels and
 * four clarifications is the screen again.
 */
const CALCULATOR_PAPERWORK: ChatQuestion = {
  id: 'paperwork',
  // Her health and the paperwork in one turn, before any question. Both are
  // read after they finish, so asking for them together means one reading
  // covers what they said and what they sent, and the questions after it are
  // only what is left. It also keeps the long answer, the conditions, out of
  // the run of quick taps.
  ask: `First, tell us about ${RECIPIENT}'s health: any conditions, and what she finds hard day to day.`,
  hint: `Talk or type. Add any paperwork too: ${DOCUMENTS_FIRST.examples
    .map(({ label }) => label[0].toLowerCase() + label.slice(1))
    .join(', ')}.`,
  // Open until they say they are done: several notes and several files.
  mode: 'say',
  multi: true,
  asides: [
    { icon: 'camera', label: 'Add paperwork', uploads: 'document' },
    {
      icon: 'chevron-right',
      label: 'Continue',
      tone: 'go',
    },
  ],
};

/**
 * The reading, in the same conversation.
 *
 * Continue does not clear the paperwork turn: the reading comes in under it,
 * with the offer and the files above. `restart` still
 * marks where the questions' history starts, so a chat opened on a later
 * question does not replay the paperwork above it. The dots under it are the
 * rest of the state.
 *
 * It was ProcessingScreen, which took the conversation away in order to say
 * something about it.
 */
const CALCULATOR_READING: ChatQuestion = {
  id: 'processing',
  ask: 'I’m just reading what you’ve given me.',
  restart: true,
  mode: 'rows',
  voice: false,
};

export const CALCULATOR_CHAT: ChatQuestion[] = [
  CALCULATOR_PAPERWORK,
  CALCULATOR_READING,
  {
    // The second-visit pattern: what we hold, said back, with the source on it.
    // Rows rather than a pick, so confirming is one tap and changing is the
    // other.
    id: 'confirm-location',
    ask: `You told us ${RECIPIENT} lives in England. Still right?`,
    mode: 'rows',
    options: ['Yes, still England', 'No, somewhere else'],
    asides: [{ label: 'Yes, still England' }, { label: 'No, somewhere else' }],
    answer: 'Yes, still England',
  },
  {
    id: 'age',
    ask: `How old is ${RECIPIENT}?`,
    mode: 'rows',
    options: ['Under 18', '18 to 59', '60 to 65', '66 or over'],
    asides: [
      { label: 'Under 18' },
      { label: '18 to 59' },
      { label: '60 to 65' },
      { label: '66 or over' },
    ],
    answer: '66 or over',
  },
  {
    // The question the claim turns on, so it is asked early and on its own.
    // Rows at four answers as well: a screener question is a tap, and a Next
    // under four short answers is a second tap for nothing.
    id: 'needs-help',
    ask: `Does ${RECIPIENT} need help from someone through the day or night?`,
    mode: 'rows',
    options: ['No', 'In the day', 'At night', 'Day and night'],
    asides: [
      { label: 'No' },
      { label: 'In the day' },
      { label: 'At night' },
      { label: 'Day and night' },
    ],
    answer: 'Day and night',
  },
  {
    // Where the letter comes in. This question and the two after it are the
    // three a conversation should not be trusted with, and all three are on the
    // letter.
    id: 'already-gets',
    //
    // Pick several, by benefit. Which part of PIP or DLA is a follow-up, asked
    // only of somebody who has it. She lives in England, so the two Scottish
    // awards are left off rather than offered and ignored.
    ask: `Does ${RECIPIENT} already get any of these?`,
    mode: 'rows',
    multi: true,
    options: [
      'Attendance Allowance',
      'PIP',
      'DLA',
      'Pension Credit',
      'None of these',
      'Not sure',
    ],
    asides: [
      {
        icon: 'camera',
        label: 'Photograph the award letter',
        uploads: 'document',
        file: 'Award letter.jpg',
      },
      { label: 'Attendance Allowance' },
      { label: 'PIP' },
      { label: 'DLA' },
      { label: 'Pension Credit' },
      { label: 'None of these' },
      { label: 'Not sure' },
      {
        icon: 'chevron-right',
        label: "Continue when you're done",
        tone: 'go',
      },
    ],
    answer: 'None of these',
  },
  {
    id: 'savings',
    ask: `Roughly, how much does ${RECIPIENT} have in savings?`,
    mode: 'rows',
    options: ['Under £6,000', '£6,000 to £16,000', 'Over £16,000', 'Not sure'],
    asides: [
      { label: 'Under £6,000' },
      { label: '£6,000 to £16,000' },
      { label: 'Over £16,000' },
      { label: 'Not sure' },
    ],
    answer: 'Under £6,000',
  },
  {
    id: 'income',
    ask: `Roughly, ${RECIPIENT}'s weekly income?`,
    mode: 'rows',
    options: ['Under £220', '£220 to £320', 'Over £320', 'Not sure'],
    asides: [
      { label: 'Under £220' },
      { label: '£220 to £320' },
      { label: 'Over £320' },
      { label: 'Not sure' },
    ],
    answer: 'Under £220',
  },
  // The condition questions, last and together. They are the ones that can be
  // talked through, and grouped at the end they stay out of the run of quick
  // taps above.
  {
    id: 'how-long',
    ask: `Has ${RECIPIENT} needed help for six months or more?`,
    mode: 'rows',
    // One thing per question. The follow-up, whether it will last a year, is
    // only for a no, so Margaret never sees it.
    options: ['Yes', 'No', 'Not sure'],
    asides: [{ label: 'Yes' }, { label: 'No' }, { label: 'Not sure' }],
    answer: 'Yes',
  },
  {
    // A route into Attendance Allowance at any age, so everyone is asked.
    id: 'terminal',
    ask: `Does ${RECIPIENT} have a terminal illness?`,
    mode: 'rows',
    options: ['Yes', 'No'],
    asides: [{ label: 'Yes' }, { label: 'No' }],
    answer: 'No',
  },
];

/** What the letter answered, read back with where each line came from. */
export const FROM_LETTER: { label: string; rows: ReviewRow[] } = {
  label: 'What we picked up',
  rows: [
    // From what they said about her health, read with the paperwork.
    {
      id: 'getting-about',
      label: 'Getting about',
      value: 'Uses a frame, has fallen at night',
      from: 'ai',
    },
    {
      id: 'aa-award',
      label: 'Attendance Allowance',
      value: 'Not in payment',
      from: 'ai',
    },
    {
      id: 'pension-credit',
      label: 'Pension Credit',
      value: 'In payment, guarantee credit',
      from: 'ai',
    },
    { id: 'income', label: 'Weekly income', value: '£218.15', from: 'ai' },
  ],
};

/** What it found, and what each one is worth. */
export const CALCULATOR_RESULT = {
  title: 'What you could claim',
  claims: [
    {
      icon: 'hand-holding-heart',
      name: 'Attendance Allowance',
      // A range, not a rate. The calculator cannot tell the higher rate from
      // the lower, and £110.40 was last year's higher rate anyway.
      description:
        '£76.70 to £114.60 a week, depending on how much help she needs.',
      lead: true,
    },
    {
      icon: 'sterling-sign',
      name: 'Pension Credit top-up',
      // No amount. The calculator says whether, not how much, so no row puts
      // a figure on it that it cannot back.
      description: 'Likely, once Attendance Allowance is in.',
    },
    {
      // The carer's own claim, blocked until the award it depends on lands.
      icon: 'heart',
      name: "Carer's Allowance, for you",
      description:
        'Once Attendance Allowance is awarded, usually six to eight weeks after.',
    },
  ],
  primary: 'Book a call',
  // The way back for somebody whose answers were wrong, rather than a second
  // way forward.
  secondary: 'Start again',
};

/**
 * Changing an answer, which is the thing async makes awkward.
 *
 * Tapping their reply opens the question's rows in a sheet, with the answer on
 * the record ticked. Picking another changes it straight away: no confirm, the
 * reply updates, and one line under it says so.
 *
 * It says the answer changed, and nothing else. What that did to the claims
 * belongs on the result screen, which is worked out again anyway: a reply that
 * starts listing what dropped out is answering a question nobody asked yet.
 */
export const CALCULATOR_NOTED = (answer: string) =>
  `Noted, that has changed to ${answer}.`;

/** Passed to the chat: the calculator's answers can be changed later. */
export const CALCULATOR_KEEP_ANSWERS = { noted: CALCULATOR_NOTED };

/**
 * The bar the calculator carries, and what its X asks.
 *
 * Leaving is cheap and the sheet says so. The flow has already promised twice
 * that answers are kept ("3 of 14 questions already answered", "Both of these
 * will be waiting for you"), so a warning about losing them here would be the
 * screen contradicting itself.
 */
export const CALCULATOR_BAR = {
  title: 'Benefits calculator',
  leave: {
    title: 'Leave the calculator?',
    body: 'Your answers are saved. You can pick this up where you left off.',
    confirm: 'Leave',
    stay: 'Keep going',
  },
};

/** The form the calculator would be if it were not a conversation. */
export const CALCULATOR_FORM = {
  title: 'Benefits calculator',
  description: 'Answer these and we will work out what you could claim.',
  primary: 'See results',
};

/** One question, ready to draw as a form field. */
export type FormField = {
  id: string;
  label: string;
  /**
   * Which control the answer takes, matched to what the live calculator draws
   * rather than to the seed's type names.
   *
   * Everything is a radio card. llama's Boolean is a RadioGroup at maxColumns
   * 2 and RadioGroup draws each option as a card, which is what the live
   * calculator shows. There is no dropdown on this form.
   *
   * The control follows the question as worded. Live asks a date of birth with
   * a date field and savings with a number field; this question set bands both
   * ("66 or over", "Under £6,000"), and a date picker under "Is their partner
   * also 66 or over?" would be nonsense. If the form should mirror live rather
   * than the proposal, the questions have to change, not the controls.
   */
  kind: 'boolean' | 'choice' | 'multi';
  options: string[];
};

/**
 * The same nineteen questions, grouped for a form.
 *
 * The grouping is the exploration's own (GROUPS and LEDGER), not one invented
 * here, so the form asks what the calculator asks in the order somebody already
 * argued for. A form has to put every question on screen before it knows a
 * single answer, which is the whole difference: the chat's conditionals prune it
 * per person and a form has nowhere to put that.
 */
export const FORM_SECTIONS: {
  id: string;
  title: string;
  fields: FormField[];
}[] = GROUPS.map((g) => ({
  id: g.id,
  title: g.title,
  fields: QUIZ_ORDER.filter((id) => LEDGER[id]?.group === g.id).map((id) => {
    const q = QUESTIONS[id];
    return {
      id: q.id,
      label: q.prompt.replace(/\{name\}/g, RECIPIENT),
      kind:
        q.type === 'multi-select'
          ? 'multi'
          : q.type === 'boolean'
            ? 'boolean'
            : 'choice',
      options:
        q.type === 'boolean'
          ? ['Yes', 'No']
          : optionsFor(q, COLD_NATION).map((o) => o.label),
    } satisfies FormField;
  }),
})).filter((g) => g.fields.length > 0);

/**
 * The same fields as one flat run, each carrying the section it came from.
 *
 * A form asked one question at a time needs an order and a position in it, which
 * a nested list cannot give without flattening it at every call site.
 */
export const FORM_FIELDS: (FormField & { section: string })[] =
  FORM_SECTIONS.flatMap((s) =>
    s.fields.map((f) => ({ ...f, section: s.title })),
  );
